import { useEffect, useRef } from 'react';
import { fabric } from 'fabric';
import { useEditorData, useEditorHistory } from '../providers/EditorProvider';
import { IFRAME_WIDGETS } from '../../../config';
import { disposeWidget, syncWidgetOverlay } from '../utils/common';
import { useHandleHistory } from './useHandleHistory';
import { useSlideSave } from '../providers/SaveProvider';
import { copyObjects, pasteObjects } from '../utils/clipboard';
import { VIDEO_LIMIT_MESSAGE } from '../utils/handleVideo';
import { useZoom } from './useZoom';
import { useSnack } from '../../../../../util/useSnack';

enum KeyboardKeys {
  Enter = 'Enter',
  Backspace = 'Backspace',
  Delete = 'Delete',
  C = 'c',
  V = 'v',
  D = 'd',
  S = 's',
  X = 'x',
  Y = 'y',
  Z = 'z',
}

const NUDGE_DIRECTIONS: Record<string, [number, number]> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
};
const NUDGE_STEP = 1;
const NUDGE_STEP_LARGE = 10;

// Keys typed into a form field (slide title, search boxes, the website URL,
// the font size input, ...) must act on that field, not on the canvas:
// no deleting, cutting, pasting or undoing slide objects.
export const isTypingInField = (target: EventTarget | null) => {
  const element = target as HTMLElement | null;
  return (
    !!element &&
    (['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName) ||
      element.isContentEditable)
  );
};

export const useHandleCopyPaste = (canvas?: fabric.Canvas | null) => {
  const { copiedObjects, setCopiedObjects } = useEditorData();
  // Read the selection when an action runs: selecting another object does
  // not necessarily re-render the components using this hook.
  const getActiveObjects = () => canvas?.getActiveObjects() || [];

  const { handleHistory } = useHandleHistory();
  const recordHistory = () => canvas && handleHistory(canvas);
  const { showErrSnack } = useSnack();

  const isEditingText = () =>
    // @ts-ignore: isEditing only exists on text objects.
    getActiveObjects().some((object) => object.isEditing);

  const handleCopy = () => {
    const activeObjects = getActiveObjects();
    if (!canvas || !activeObjects.length) return;
    setCopiedObjects(copyObjects(canvas, activeObjects));
  };

  const handleCut = () => {
    if (isEditingText()) return;
    handleCopy();
    handleDelete();
  };

  const handleDuplicate = async () => {
    const activeObjects = getActiveObjects();
    if (!canvas || !activeObjects.length || isEditingText()) return;
    const { skippedVideos } = await pasteObjects(
      canvas,
      copyObjects(canvas, activeObjects),
      recordHistory,
    );
    if (skippedVideos) showErrSnack(VIDEO_LIMIT_MESSAGE);
  };

  const handlePaste = async () => {
    if (!canvas || !copiedObjects.length || isEditingText()) return;
    const { shifted, skippedVideos } = await pasteObjects(
      canvas,
      copiedObjects,
      recordHistory,
    );
    setCopiedObjects(shifted);
    if (skippedVideos) showErrSnack(VIDEO_LIMIT_MESSAGE);
  };

  const handleDelete = () => {
    const activeObjects = canvas?.getActiveObjects() || [];
    activeObjects.forEach((object) => {
      // @ts-ignore:
      if (object.type === 'i-text' && object?.isEditing) {
        return;
      }

      const currentObj = object.toObject(['data']);

      if (Object.values(IFRAME_WIDGETS).includes(object?.data?.type)) {
        const deleteElements = document.getElementsByClassName(
          object?.data?.className,
        );
        const elementsArray = Array.prototype.slice.call(deleteElements);

        elementsArray.forEach((element) => {
          element.remove();
        });
        disposeWidget(canvas, object?.data?.className);
      }

      if (currentObj.data?.type === 'video') {
        currentObj.data.deleteVideo();
      }

      canvas?.remove(object);
    });
    canvas?.requestRenderAll();
  };

  return {
    handleCopy,
    handlePaste,
    handleCut,
    handleDelete,
    handleDuplicate,
    copiedObjects,
  };
};

export const useKeyboardEvents = () => {
  const { canvas } = useEditorData();
  const { undo, redo } = useEditorHistory();
  const { handleCopy, handlePaste, handleCut, handleDelete, handleDuplicate } =
    useHandleCopyPaste(canvas);
  const { handleHistory } = useHandleHistory();
  const { save } = useSlideSave();
  const { zoomIn, zoomOut, zoomToFit } = useZoom();
  const nudged = useRef(false);

  // Arrow keys move the selection by one slide pixel (ten with Shift).
  const nudge = (event: KeyboardEvent) => {
    const direction = NUDGE_DIRECTIONS[event.key];
    const object = canvas?.getActiveObject();
    // @ts-ignore: isEditing only exists on text objects.
    if (!canvas || !direction || !object || object.isEditing) return false;
    event.preventDefault();
    const step = event.shiftKey ? NUDGE_STEP_LARGE : NUDGE_STEP;
    object.set({
      left:
        (object.left || 0) + (object.lockMovementX ? 0 : direction[0] * step),
      top: (object.top || 0) + (object.lockMovementY ? 0 : direction[1] * step),
    });
    object.setCoords();
    canvas
      .getActiveObjects()
      .filter((target) =>
        Object.values(IFRAME_WIDGETS).includes(target.data?.type),
      )
      .forEach((target) => syncWidgetOverlay(canvas, target));
    canvas.requestRenderAll();
    nudged.current = true;
    return true;
  };

  // Holding an arrow key repeats keydown; record one undo step on release.
  const handleKeyUp = (event: KeyboardEvent) => {
    if (nudged.current && NUDGE_DIRECTIONS[event.key] && canvas) {
      nudged.current = false;
      handleHistory(canvas);
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    const inField = isTypingInField(event.target);
    if ((event.ctrlKey || event.metaKey) && event.key === KeyboardKeys.S) {
      // Also while typing in a field: blurring applies the field's value
      // first (fields commit on blur).
      event.preventDefault();
      if (inField) (event.target as HTMLElement).blur();
      setTimeout(() => save());
      return;
    }
    if (!inField && !event.ctrlKey && !event.metaKey && nudge(event)) {
      return;
    }
    if (
      !inField &&
      (event.key === KeyboardKeys.Backspace ||
        event.key === KeyboardKeys.Delete)
    ) {
      handleDelete();
    }

    if (
      event.key === KeyboardKeys.Enter &&
      canvas?.getActiveObject()?.type !== 'i-text'
    ) {
      event.preventDefault();
    }
    if ((event.ctrlKey || event.metaKey) && !inField) {
      switch (event.key) {
        case KeyboardKeys.C:
          handleCopy();
          break;
        case KeyboardKeys.X:
          handleCut();
          break;
        case KeyboardKeys.V:
          handlePaste();
          break;
        case KeyboardKeys.D:
          handleDuplicate();
          break;
        case KeyboardKeys.Z:
          if (event.shiftKey) {
            redo();
          } else {
            undo();
          }
          break;
        // Zoom the slide instead of the page.
        case '=':
        case '+':
          event.preventDefault();
          zoomIn();
          break;
        case '-':
          event.preventDefault();
          zoomOut();
          break;
        case '0':
          event.preventDefault();
          zoomToFit();
          break;
      }

      event.stopPropagation();
      return false;
    }
  };

  // Subscribe once and always call the latest handler (the handlers are
  // recreated every render, which used to re-subscribe on every render).
  const handleKeyDownRef = useRef(handleKeyDown);
  handleKeyDownRef.current = handleKeyDown;
  const handleKeyUpRef = useRef(handleKeyUp);
  handleKeyUpRef.current = handleKeyUp;
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => handleKeyDownRef.current(event);
    const onKeyUp = (event: KeyboardEvent) => handleKeyUpRef.current(event);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);
};
